import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button, Label, Switch } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { SupplierTable } from './components';
import type { Supplier } from './components/suppliersMeta';

type SuppliersPageViewProps = {
    data: Supplier[];
    includeArchived: boolean;
    onSetArchived: (id: string, archived: boolean) => void;
    onToggleArchived: (next: boolean) => void;
    onAddSupplier: () => void;
    onDeleteSupplier: (id: string) => void;
    onEditSupplier: (id: string) => void;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    onRecordPayment: (id: string) => void;
    onSearchChange: (value: string) => void;
    onViewSupplier: (id: string) => void;
    page: number;
    pageSize: number;
    search: string;
    totalCount: number;
    totalPages: number;
};

export const SuppliersPageView: React.FC<SuppliersPageViewProps> = ({
    data,
    includeArchived,
    onSetArchived,
    onToggleArchived,
    onAddSupplier,
    onDeleteSupplier,
    onEditSupplier,
    onPageChange,
    onPageSizeChange,
    onRecordPayment,
    onSearchChange,
    onViewSupplier,
    page,
    pageSize,
    search,
    totalCount,
    totalPages,
}) => {
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            searchPlaceholder: t(
                'suppliers.components.supplierTable.searchPlaceholder',
            ),
            searchAria: t('suppliers.components.supplierTable.searchAria'),
            addSupplier: t('suppliers.components.supplierTable.addSupplier'),
            tableAria: t('suppliers.components.supplierTable.tableAria'),
            showArchived: t('suppliers.components.supplierTable.showArchived'),
        }),
        [t],
    );

    return (
        <section
            aria-label={copy.tableAria}
            {...getNodeTestIdProp('supplier-table-section')}
        >
            <div
                className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
                {...getNodeTestIdProp('supplier-table-toolbar')}
            >
                <label
                    className="flex w-full items-center gap-2 rounded-xl border border-border bg-surface-secondary px-3 py-2 sm:w-auto sm:shrink-0"
                    htmlFor="supplier-table-search"
                    {...getNodeTestIdProp('supplier-table-search-wrap')}
                >
                    <Icon
                        aria-hidden
                        className="size-4 text-muted"
                        icon="gravity-ui:magnifier"
                    />
                    <input
                        aria-label={copy.searchAria}
                        className="w-full bg-transparent text-sm text-foreground placeholder:text-muted focus:outline-none sm:w-56"
                        onChange={(event) => onSearchChange(event.target.value)}
                        placeholder={copy.searchPlaceholder}
                        type="text"
                        value={search}
                        id="supplier-table-search"
                        {...getNodeTestIdProp('supplier-table-search')}
                    />
                </label>
                <div className="flex items-center gap-3">
                    <Switch
                        isSelected={includeArchived}
                        onChange={onToggleArchived}
                        {...getNodeTestIdProp('supplier-table-show-archived')}
                    >
                        <Label className="text-sm text-muted">
                            {copy.showArchived}
                        </Label>
                    </Switch>
                    <Button
                        onPress={onAddSupplier}
                        variant="primary"
                        {...getNodeTestIdProp('supplier-table-add')}
                    >
                        <Icon icon="gravity-ui:plus" />
                        {copy.addSupplier}
                    </Button>
                </div>
            </div>
            <SupplierTable
                data={data}
                onDeleteSupplier={onDeleteSupplier}
                onEditSupplier={onEditSupplier}
                onPageChange={onPageChange}
                onPageSizeChange={onPageSizeChange}
                onRecordPayment={onRecordPayment}
                onSetArchived={onSetArchived}
                onViewSupplier={onViewSupplier}
                page={page}
                pageSize={pageSize}
                totalCount={totalCount}
                totalPages={totalPages}
            />
        </section>
    );
};
